import { getPlanLabel } from "./plans.js";
import { normalizePreferences } from "./preferences.js";
import { onboardingGiftLabel, resolveGiftedDays } from "./onboarding-gift.js";

function normalizeOnboardingMessagePlan(plan) {
  const normalized = String(plan || "").trim().toLowerCase();
  return ["free", "fun", "pro"].includes(normalized) ? normalized : "";
}

function buildSummaryDefaultsCopy(preferences = {}) {
  const normalized = normalizePreferences(preferences);
  if (normalized.minEnabled === false) {
    return "Min is off right now.";
  }
  const inboundFeatures = [];
  if (normalized.inboundSummary) inboundFeatures.push("summaries");
  if (normalized.inboundTranscript) inboundFeatures.push("transcripts");
  const inboundCopy = inboundFeatures.length
    ? `Inbound ${inboundFeatures.join(" and ")} are on.`
    : "Inbound summaries and transcripts are off.";
  const outboundCopy = normalized.outboundSummary
    ? "Outbound summaries are on."
    : "Outbound summaries are off.";
  return [inboundCopy, outboundCopy].filter(Boolean).join(" ");
}

function buildPreferencesHint(preferences = {}) {
  const normalized = normalizePreferences(preferences);
  if (normalized.minEnabled === false) {
    return "Preferences is where you can turn Min back on, change transcripts, chat rules, and branding controls.";
  }
  const approvalEnabled = !normalized.inboundAutomatic || !normalized.outboundAutomatic;
  if (normalized.inboundTranscript || normalized.outboundTranscript) {
    return approvalEnabled
      ? "React with Ⓜ️ when you want Min to run. Preferences is where you can change transcripts, chat rules, and branding controls."
      : "Preferences is where you can change transcripts, chat rules, and branding controls.";
  }
  return approvalEnabled
    ? "React with Ⓜ️ when you want Min to run. Preferences is where you can turn on transcripts, chat rules, and branding controls."
    : "Preferences is where you can turn on transcripts, chat rules, and branding controls.";
}

function buildGiftedTitle(gift = {}) {
  const days = resolveGiftedDays(gift);
  if (days === 38 || (days > 0 && days % 31 !== 0)) return `${onboardingGiftLabel(gift)}.`;
  const normalizedGiftedMonths = days / 31;
  if (normalizedGiftedMonths >= 2) {
    return `Welcome to Min. You now have ${normalizedGiftedMonths} free months of Fun.`;
  }
  if (normalizedGiftedMonths === 1) {
    return "Welcome to Min. You now have 1 free month of Fun.";
  }
  return "Your self chat is ready.";
}

export function buildSelfChatPreviewMessages({ giftedDays, giftedMonths = 1, preferences = {}, relinkUrl = "" } = {}) {
  const normalizedRelinkUrl = String(relinkUrl || "").trim();
  const messages = [
    {
      from: "Min",
      text: `${buildGiftedTitle({ giftedDays, giftedMonths })} ${buildSummaryDefaultsCopy(preferences)}`,
    },
    { from: "You", text: "Voice note (0:15)" },
    {
      from: "Min",
      text: `Summary: Send yourself a test voice note here${normalizePreferences(preferences).outboundAutomatic === false ? " and react with Ⓜ️" : ""}. Min will reply in this same chat. ${buildPreferencesHint(preferences)}`,
    },
  ];
  if (normalizedRelinkUrl) {
    messages.push({
      from: "Min",
      text: `Relink this WhatsApp number anytime: ${normalizedRelinkUrl}`,
    });
  }
  return messages;
}

export function buildSelfChatPlanSummary(plan, { giftedDays, giftedMonths = 0, preferences = {} } = {}) {
  const normalizedPlan = normalizeOnboardingMessagePlan(plan) || String(plan || "").trim().toLowerCase();
  const summaryDefaultsCopy = buildSummaryDefaultsCopy(preferences);
  if (normalizedPlan === "free") {
    return {
      title: "Free plan",
      copy: `${summaryDefaultsCopy} Upgrade later from Billing when you want more control.`,
    };
  }
  if (normalizedPlan === "fun") {
    return {
      title: onboardingGiftLabel({ giftedDays, giftedMonths }),
      copy: `${summaryDefaultsCopy} A week when they join. A month of their plan when they first pay.`,
    };
  }
  if (normalizedPlan === "pro") {
    return {
      title: "Pro plan",
      copy: `${summaryDefaultsCopy} Pro also gives branding removal and per-chat controls right away.`,
    };
  }
  return {
    title: `${getPlanLabel(normalizedPlan || "free")} plan`,
    copy: "Your setup is ready in this WhatsApp self chat.",
  };
}
